import { useCallback } from 'react'
import type { apiClient } from '~/shared/api/data-ingestion-api'
import { useCreatePaPatientMatch } from '../api'
import type { PatientMatch } from './types'
import { useSubmitPaMatchingReview } from './use-submit-pa-matching-review'

function mapToPaRecordBody(match: PatientMatch): apiClient.PatientPaRecordWithNotes {
  return {
    patient_first_name: match.patient_first_name,
    patient_last_name: match.patient_last_name,
    patient_dob: match.date_of_birth,
    service_date: match.lims_date_of_service,
    policy_number: match.policy_number,
    case_id: match.lims_casefile_id,
    payor_id: match.payorname,
    test_name: match.test_name,
    cpt_codes: match.allproccodes_claim,
    vendor_given_to: match.pa_vendor,
    reference_token: match.reference_number,
    authorization_token: match.authorization_code,
    notes: match.notes,
  }
}

export function useCreatePaPatientMatchAndValidate({ documentKey }: { documentKey: string }) {
  const { mutateAsync: createPaPatientMatch } = useCreatePaPatientMatch()
  const { submitPaMatchingReview } = useSubmitPaMatchingReview({ documentKey })

  const createPaPatientMatchAndValidate = useCallback(
    async (patientMatch: PatientMatch) => {
      const patientRecord = await createPaPatientMatch({
        path: { key: documentKey },
        body: mapToPaRecordBody(patientMatch),
      })
      if (
        patientRecord.success &&
        patientRecord.index !== undefined &&
        patientRecord.index !== null
      ) {
        await submitPaMatchingReview({
          match_status: 'validated',
          selected_match_index: patientRecord.index,
        })
      } else {
        throw new Error(`Failed to create PA patient match: ${patientRecord.message}`)
      }
    },
    [createPaPatientMatch, documentKey, submitPaMatchingReview],
  )

  return {
    createPaPatientMatchAndValidate,
  }
}
